import { toast } from "@jsq/ds/core";
import { type CrmGeneratePresignedUrlsMutationFn } from "~/graphql/supergraphHooks";
import { Severity, TOOL_TYPE_TO_DISPLAY_MAPPING } from "../../constants";
import { CRM_DATA_TYPE_CONFIG, type CrmDataType } from "./constants";
import {
  FileValidationData,
  ToolValidation,
  ValidationData,
  GeneratePresignedUrlsInput,
  ErrorHandlingResult,
} from "./types";

/**
 * Gets display name for a tool type using the new config
 * Falls back to TOOL_TYPE_TO_DISPLAY_MAPPING for backward compatibility
 */
export const getDisplayNameForToolType = (toolType: string): string => {
  // First try the new config
  const config = Object.values(CRM_DATA_TYPE_CONFIG).find((c) => c.toolType === toolType);
  if (config) {
    return config.displayName;
  }

  // Fallback to existing mapping for backward compatibility
  return TOOL_TYPE_TO_DISPLAY_MAPPING[toolType] || toolType;
};

/**
 * Gets data type key by tool type
 */
export const getDataTypeByToolType = (toolType: string): CrmDataType | undefined => {
  return Object.entries(CRM_DATA_TYPE_CONFIG).find(
    ([_, config]) => config.toolType === toolType,
  )?.[0];
};

/**
 * Sorts tools by predefined priority order
 * Accepts objects with either 'toolType' or 'tool_type' property
 */
export const sortToolsByPriority = <T extends { toolType?: string; tool_type?: string }>(
  tools: T[],
): T[] => {
  return tools.sort((a, b) => {
    const toolTypeA = a.toolType || a.tool_type || "";
    const toolTypeB = b.toolType || b.tool_type || "";

    const dataTypeA = getDataTypeByToolType(toolTypeA);
    const dataTypeB = getDataTypeByToolType(toolTypeB);
    const orderA = dataTypeA ? CRM_DATA_TYPE_CONFIG[dataTypeA]?.order || 999 : 999;
    const orderB = dataTypeB ? CRM_DATA_TYPE_CONFIG[dataTypeB]?.order || 999 : 999;
    return orderA - orderB;
  });
};

/**
 * Gets validation key for a data type
 */
export const getValidationKeyForDataType = (dataType: CrmDataType): string => {
  return CRM_DATA_TYPE_CONFIG[dataType]?.validationKey || dataType;
};

/**
 * Downloads a file from S3 using presigned URL
 */
export const downloadFileFromS3 = async (
  s3Key: string,
  fileName: string,
  fileType: "errors" | "mapped",
  validationData: ValidationData | null,
  generatePresignedUrlsMutation: CrmGeneratePresignedUrlsMutationFn,
  downloadingFiles: Set<string>,
  setDownloadingFiles: React.Dispatch<React.SetStateAction<Set<string>>>,
): Promise<void> => {
  if (!validationData) {
    toast.error("Validation data not available");
    return;
  }

  // Create a unique key for tracking this download
  const downloadKey = `${fileName}-${fileType}`;

  // Prevent multiple downloads of the same file
  if (downloadingFiles.has(downloadKey)) {
    return;
  }

  // Add to downloading set
  setDownloadingFiles((prev) => new Set(prev).add(downloadKey));

  try {
    const input: GeneratePresignedUrlsInput = {
      environment: validationData.environment,
      jobType: "CRM_IMPORT",
      arenaDomain: validationData.arenaDomain,
      arenaUserEmail: validationData.arenaUserEmail,
      files: [
        {
          filename: fileName,
          contentType: "application/octet-stream",
          contentLength: 0, // Not needed for download
          operation: "DOWNLOAD",
          s3Key: s3Key,
        },
      ],
    };

    const response = await generatePresignedUrlsMutation({
      variables: { input },
    });

    const result = response.data?.dataStudioGeneratePresignedUrls;

    if (!result?.success || !result.presignedUrlObjs?.[0]) {
      const errorMessage = result?.errors?.join(", ") || "Failed to generate download URL";
      throw new Error(errorMessage);
    }

    const downloadUrl = result.presignedUrlObjs[0].s3Url;

    // Create a temporary link to trigger download
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = fileName;
    link.target = "_blank"; // Open in new tab/window
    link.rel = "noopener noreferrer"; // Security best practice
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success(`Downloading ${fileType} file: ${fileName}`);
  } catch (_error) {
    toast.error(`Failed to download ${fileType} file: ${fileName}`);
  } finally {
    // Remove from downloading set
    setDownloadingFiles((prev) => {
      const newSet = new Set(prev);
      newSet.delete(downloadKey);
      return newSet;
    });
  }
};

/**
 * Handles downloading error files for a specific file
 */
export const handleDownloadErrors = (
  fileName: string,
  validationData: ValidationData,
  downloadFileFromS3Fn: (
    s3Key: string,
    fileName: string,
    fileType: "errors" | "mapped",
  ) => Promise<void>,
): void => {
  // Find the tool validation for this file to get the s3IssuesPath
  const toolValidation = validationData?.toolValidations?.find(
    (tool) => tool.fileName === fileName,
  );

  if (toolValidation?.s3IssuesPath) {
    // Extract S3 key from the full S3 path
    const s3Key = toolValidation.s3IssuesPath.split("/").slice(3).join("/");
    void downloadFileFromS3Fn(s3Key, fileName, "errors");
  } else {
    toast.error(`No error file available for ${fileName}`);
  }
};

/**
 * Handles downloading mapped files for a specific file
 */
export const handleDownloadMappedFile = (
  fileName: string,
  validationData: ValidationData,
  downloadFileFromS3Fn: (
    s3Key: string,
    fileName: string,
    fileType: "errors" | "mapped",
  ) => Promise<void>,
): void => {
  // Find the tool validation for this file to get the s3FilePath
  const toolValidation = validationData?.toolValidations?.find(
    (tool) => tool.fileName === fileName,
  );

  if (toolValidation?.s3FilePath) {
    // Extract S3 key from the full S3 path
    const s3Key = toolValidation.s3FilePath.split("/").slice(3).join("/");
    void downloadFileFromS3Fn(s3Key, fileName, "mapped");
  } else {
    toast.error(`No mapped file available for ${fileName}`);
  }
};

/**
 * Transforms validation data from API format to display format
 */
export const transformValidationDataForDisplay = (
  toolValidations: ToolValidation[] | undefined,
): FileValidationData[] => {
  return (
    toolValidations?.map((tool) => ({
      fileName: tool.fileName,
      dataType: getDisplayNameForToolType(tool.toolType),
      toolType: tool.toolType,
      totalRecords: tool.stats.totalRecords,
      validRecords: tool.stats.validRecords,
      errorCount: tool.stats.errorCount,
      warningCount: tool.stats.warningCount,
      issues: tool.issues.map((issue) => ({
        row: issue.rowNumber || 0,
        field: issue.column,
        issue: issue.message,
        severity: issue.severity === "ERROR" ? Severity.ERROR : Severity.WARNING,
      })),
    })) || []
  );
};

/**
 * Creates a function to check if a file is currently being downloaded
 */
export const createDownloadChecker = (downloadingFiles: Set<string>) => ({
  isDownloadingErrors: (fileName: string): boolean =>
    downloadingFiles.has(`${fileName}-errors`),
  isDownloadingMappedFile: (fileName: string): boolean =>
    downloadingFiles.has(`${fileName}-mapped`),
});

/**
 * Determines error handling and placeholder logic for validation states
 */
export const determineValidationDisplayState = (
  validationError: any,
  shouldShowValidationResults: boolean,
  validationLoading: boolean,
  validationData: { jobStatus?: string } | null | undefined,
): ErrorHandlingResult => {
  // Check for business logic errors vs network errors
  const isBusinessLogicError = validationError?.name === "BusinessLogicError";

  // Show error overlay only for network/technical errors, not business logic errors
  if (validationError && !isBusinessLogicError) {
    return {
      shouldShowErrorOverlay: true,
      shouldShowPlaceholder: false,
    };
  }

  // Show placeholder if validation is not yet complete or if there's a business logic error
  if (!shouldShowValidationResults || isBusinessLogicError) {
    // Check if the job has failed
    const isJobFailed = validationData?.jobStatus === "FAILED";

    // Determine the appropriate message based on job status, loading state, and error type
    let placeholderMessage: string;
    let showLoader = true;

    if (isBusinessLogicError) {
      // Handle business logic errors from the API
      placeholderMessage = `Validation error: ${validationError.message}. Please try refreshing or contact support if the issue persists.`;
      showLoader = false;
    } else if (validationLoading) {
      placeholderMessage = "Checking status...";
    } else if (isJobFailed) {
      placeholderMessage =
        "Data validation failed. Please check your uploaded files and try again, or contact support if the issue persists.";
      showLoader = false;
    } else {
      placeholderMessage =
        "We are validating your data files. This would usually take about 2-5 minutes, depending on the size of your data. You may navigate to the dashboard and check in after a few minutes";
    }

    return {
      shouldShowErrorOverlay: false,
      shouldShowPlaceholder: true,
      placeholderMessage,
      showLoader,
    };
  }

  // Show validation results
  return {
    shouldShowErrorOverlay: false,
    shouldShowPlaceholder: false,
  };
};
